import React, { useState, useEffect } from 'react';
import { createMap, updateMap, deleteMap, getMedia } from '../services/api';
import { useOrganizationChange } from '../contexts/GlobalContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Grid,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';

const MapManager = ({ maps, organizations, onMapSelect, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organization: '',
    background_image_source: 'new', // 'new' or 'existing'
    existing_background_image: '',
    background_image: null,
    meters_per_pixel: '0.042',
    default_zoom: '22.0',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      organization: '',
      background_image_source: 'new',
      existing_background_image: '',
      background_image: null,
      meters_per_pixel: '0.042',
      default_zoom: '22.0',
    });
    setEditingMap(null);
    setErrors({});
    setIsDragOver(false);
  };

  useEffect(() => {
    loadBackgroundImages();
  }, []);

  // Reload background images when organization changes
  useOrganizationChange(() => {
    loadBackgroundImages();
  }, []);

  const loadBackgroundImages = async () => {
    try {
      const response = await getMedia();
      const images =
        response?.results?.filter(
          item => item.media_type === 'map_background'
        ) || [];
      setBackgroundImages(images);
    } catch (error) {
      console.error('Error loading background images:', error);
    }
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = map => {
    // Check if this map has a background image
    const hasBackgroundImage = map.background_image && map.background_image.id;

    setFormData({
      name: map.name,
      description: map.description,
      organization: map.organization,
      background_image_source: hasBackgroundImage ? 'existing' : 'new',
      existing_background_image: hasBackgroundImage
        ? `background_${map.background_image.id}`
        : '',
      background_image: null,
      meters_per_pixel: map.meters_per_pixel || '0.042',
      default_zoom: map.default_zoom || '22.0',
    });
    setEditingMap(map);
    setShowForm(true);
  };

  const handleChange = e => {
    if (e.target.type === 'file') {
      setFormData({
        ...formData,
        [e.target.name]: e.target.files[0],
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setFormData({
          ...formData,
          background_image: file,
          background_image_source: 'new',
        });
      } else {
        alert('Please drop only image files.');
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        organization: formData.organization,
        meters_per_pixel: parseFloat(formData.meters_per_pixel),
        default_zoom: parseFloat(formData.default_zoom),
      };

      // Handle background image based on source
      if (
        formData.background_image_source === 'existing' &&
        formData.existing_background_image
      ) {
        // For existing images, just reference by ID
        const backgroundImageId = formData.existing_background_image.replace(
          'background_',
          ''
        );
        submitData.background_image_id = parseInt(backgroundImageId);
      } else if (
        formData.background_image_source === 'new' &&
        formData.background_image
      ) {
        // For new images, we need to create a background image first
        const backgroundImageData = new FormData();
        backgroundImageData.append('name', `Background for ${formData.name}`);
        backgroundImageData.append(
          'description',
          `Background image for map: ${formData.name}`
        );
        backgroundImageData.append('organization', formData.organization);
        backgroundImageData.append('image_file', formData.background_image);

        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch('/api/background-images/', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: backgroundImageData,
          });

          if (!response.ok) {
            throw new Error('Failed to create background image');
          }

          const backgroundImage = await response.json();
          submitData.background_image_id = backgroundImage.id;
        } catch (bgError) {
          console.error('Error creating background image:', bgError);
          setErrors({
            background_image: [
              'Failed to upload background image. Please try again.',
            ],
          });
          setLoading(false);
          return;
        }
      }

      if (editingMap) {
        await updateMap(editingMap.id, submitData);
      } else {
        await createMap(submitData);
      }

      setShowForm(false);
      resetForm();
      onRefresh();
      loadBackgroundImages(); // Refresh background images list
    } catch (error) {
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async map => {
    if (window.confirm(`Are you sure you want to delete "${map.name}"?`)) {
      try {
        await deleteMap(map.id);
        onRefresh();
      } catch (error) {
        alert('Error deleting map. Please try again.');
      }
    }
  };

  return (
    <div className="map-manager">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          Maps
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          size="large"
        >
          Create New Map
        </Button>
      </Box>

      {showForm && (
        <div className="form-modal">
          <div className="form-card">
            <div className="form-header">
              <h3>{editingMap ? 'Edit Map' : 'Create New Map'}</h3>
              <button
                onClick={() => setShowForm(false)}
                className="close-button"
              >
                ×
              </button>
            </div>

            {errors.general && (
              <div className="error-message">{errors.general}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                {errors.name && (
                  <span className="field-error">{errors.name.join(', ')}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description:</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
                {errors.description && (
                  <span className="field-error">
                    {errors.description.join(', ')}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="organization">Organization:</label>
                <select
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select an organization</option>
                  {(Array.isArray(organizations?.results)
                    ? organizations.results
                    : []
                  ).map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {errors.organization && (
                  <span className="field-error">
                    {errors.organization.join(', ')}
                  </span>
                )}
              </div>

              <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Background Image
                </FormLabel>
                <RadioGroup
                  name="background_image_source"
                  value={formData.background_image_source}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel
                    value="existing"
                    control={<Radio />}
                    label="Select from existing images"
                  />
                  <FormControlLabel
                    value="new"
                    control={<Radio />}
                    label="Upload new image"
                  />
                </RadioGroup>

                {/* Existing Images Dropdown */}
                {formData.background_image_source === 'existing' && (
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel id="existing-background-image-label">
                        Select background image
                      </InputLabel>
                      <Select
                        labelId="existing-background-image-label"
                        name="existing_background_image"
                        value={formData.existing_background_image}
                        onChange={handleChange}
                        label="Select background image"
                      >
                        {backgroundImages.map(image => (
                          <MenuItem key={image.id} value={image.id}>
                            {image.name} ({image.organization.name})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {backgroundImages.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        No existing background images found. Upload a new one
                        below.
                      </Typography>
                    )}
                  </Box>
                )}

                {/* New Image Upload with Drag & Drop */}
                {formData.background_image_source === 'new' && (
                  <Box>
                    <Box
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById('background_image').click()
                      }
                      sx={{
                        border: theme =>
                          `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        bgcolor: isDragOver
                          ? 'action.hover'
                          : 'background.default',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {formData.background_image ? (
                        <Box>
                          <Typography
                            variant="body1"
                            color="success.main"
                            fontWeight="bold"
                            gutterBottom
                          >
                            File selected: {formData.background_image.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Click to change or drag a new file here
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body1" gutterBottom>
                            Drag & drop an image here, or click to browse
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Supports: JPG, PNG, GIF, WebP
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <input
                      type="file"
                      id="background_image"
                      name="background_image"
                      onChange={handleChange}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                  </Box>
                )}

                {errors.background_image && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {errors.background_image.join(', ')}
                  </Typography>
                )}
              </FormControl>

              <div className="form-group">
                <label htmlFor="meters_per_pixel">Meters per Pixel:</label>
                <input
                  type="number"
                  step="any"
                  id="meters_per_pixel"
                  name="meters_per_pixel"
                  value={formData.meters_per_pixel}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="default_zoom">Default Zoom Level:</label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="30"
                  id="default_zoom"
                  name="default_zoom"
                  value={formData.default_zoom}
                  onChange={handleChange}
                />
                <small className="field-help">
                  Initial zoom level for this map (higher = more zoomed in)
                </small>
                {errors.default_zoom && (
                  <span className="field-error">
                    {errors.default_zoom.join(', ')}
                  </span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? 'Saving...' : editingMap ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="maps-grid">
        {(() => {
          // Handle both paginated responses and direct arrays
          const mapsArray = Array.isArray(maps)
            ? maps
            : Array.isArray(maps?.results)
              ? maps.results
              : [];

          if (mapsArray.length === 0) {
            return (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No maps found. Create your first map to get started!
                </Typography>
              </Box>
            );
          }

          return (
            <Grid container spacing={3}>
              {mapsArray.map(map => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={map.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition:
                        'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    {map.background_image_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={map.background_image_url}
                        alt={map.name}
                        sx={{
                          objectFit: 'cover',
                          bgcolor: 'background.default',
                        }}
                      />
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {map.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        {map.description}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Created:{' '}
                          {new Date(map.created_at).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={`${map.map_data_count} data points`}
                          size="small"
                          variant="outlined"
                          sx={{ width: 'fit-content' }}
                        />
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => onMapSelect(map)}
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEdit(map)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(map)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        })()}
      </div>
    </div>
  );
};

export default MapManager;
